import { z } from 'zod';
import { OrgRole } from './org.js';
/**
 * OnboardingSeedStatus — per-org status doc for the post-Confirm seed
 * pipeline. One doc per org (`id === orgId`). Written initially by the
 * `PUT /business-context` handler when `onboardingCompleted` flips true
 * for the first time; updated by the `onboarding.completed` Pub/Sub
 * subscriber as each parallel seed task completes.
 *
 * The wizard's "Warming up your home" interstitial subscribes to this
 * doc with a Firestore real-time listener and renders the step checklist
 * + agent-bubble narration directly from the per-step states. When
 * `status === 'complete'` OR a 15s ceiling elapses, the interstitial
 * redirects to `/today`.
 *
 * Failures don't block: a step in `failed` is rendered as a red row in
 * the interstitial with a one-line reason; the redirect still happens.
 * The Briefing page also subscribes (so a late-arriving welcome note
 * pops in without a refresh).
 */
export const ONBOARDING_SEED_STATUS_COLLECTION = 'onboardingSeedStatus';
export const SeedStepState = z.enum(['pending', 'running', 'done', 'failed']);
export const SeedOverallStatus = z.enum(['pending', 'running', 'complete', 'partial', 'failed']);
/** Aggregated invite-row counters; the interstitial renders "3 of 5 sent". */
export const SeedInvitesProgress = z.object({
    total: z.number().int().min(0),
    sent: z.number().int().min(0),
    failed: z.number().int().min(0),
    state: SeedStepState,
});
export const OnboardingSeedStatus = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** Uid of the operator who completed onboarding — needed by the
     *  Firestore-trigger seeder for audit + note authorship since the
     *  trigger has no request context. */
    initiatedByUid: z.string().min(1),
    /** Org display name, captured at init time so the seeder doesn't
     *  need a second read on `organizations/{orgId}` from the trigger. */
    orgName: z.string().min(1),
    /** Transient capture of the invite rows the wizard submitted; the
     *  seeder reads them once at the start of `fireInvites` then leaves
     *  them in place as a record of what was sent. Cleared by a
     *  follow-up admin endpoint if needed. */
    pendingInvites: z
        .array(z.object({
        email: z.string().email(),
        role: OrgRole,
    }))
        .default([]),
    status: SeedOverallStatus,
    steps: z.object({
        welcomeNote: SeedStepState,
        companyPin: SeedStepState,
        refreshSignals: SeedStepState,
        invites: SeedInvitesProgress,
    }),
    /** Per-step failure reasons. Keyed by step name. */
    failureReasons: z.record(z.string(), z.string()).default({}),
    startedAt: z.string(),
    completedAt: z.string().optional(),
    /** Last update timestamp — used for "is this stuck?" auditing. */
    updatedAt: z.string(),
});
/**
 * Initial doc shape written by the handler. All steps start `pending`;
 * the Firestore-trigger seeder flips them to `running` → `done`/`failed`.
 */
export function buildInitialSeedStatus(opts) {
    return {
        id: opts.orgId,
        orgId: opts.orgId,
        initiatedByUid: opts.initiatedByUid,
        orgName: opts.orgName,
        pendingInvites: opts.pendingInvites,
        status: 'pending',
        steps: {
            welcomeNote: 'pending',
            companyPin: 'pending',
            refreshSignals: 'pending',
            invites: {
                total: opts.pendingInvites.length,
                sent: 0,
                failed: 0,
                state: 'pending',
            },
        },
        failureReasons: {},
        startedAt: opts.nowIso,
        updatedAt: opts.nowIso,
    };
}
/**
 * Derive the overall `status` from per-step states. Called by the
 * subscriber every time it updates a step; keeps the overall field in
 * sync without manual orchestration.
 */
export function deriveOverallStatus(steps) {
    const states = [
        steps.welcomeNote,
        steps.companyPin,
        steps.refreshSignals,
        steps.invites.state,
    ];
    if (states.every((s) => s === 'pending'))
        return 'pending';
    if (states.some((s) => s === 'running' || s === 'pending')) {
        return 'running';
    }
    const failed = states.filter((s) => s === 'failed').length;
    const done = states.filter((s) => s === 'done').length;
    if (failed === states.length)
        return 'failed';
    if (done === states.length)
        return 'complete';
    return 'partial';
}
